<template>

  <div
    class="video-player-root"
    :class="{ 'video-player-root--preview-tabs': previewSubtitleTabs }"
  >

    <div class="video-player-wrapper" @contextmenu.prevent>
      <videoPlayer
        v-if="!!url"
        class="video-player vjs-custom-skin"
        ref="videoPlayer"
        :playsinline="true"
        :options="playerOptions"
        @contextmenu.prevent
        @ready="onPlayerReady"
        @play="onPlayerPlay"
        @pause="onPlayerPause"
        @ended="onPlayerEnded"
        @error="onError"
      />
    </div>

    <div v-if="previewSubtitleTabs" class="video-player-subtitle-block">
      <div class="video-player-subtitle-row">
        <span class="video-player-subtitle-label">字幕</span>
        <div class="video-player-subtitle-tabs">
          <button
            v-for="item in subtitleTabOptions"
            :key="item.value"
            type="button"
            class="video-player-subtitle-tab"
            :class="{
              'video-player-subtitle-tab--active': subtitleActive === item.value
            }"
            @click="handleSubtitleTabClick(item.value)"
          >
            {{ item.label }}
          </button>
        </div>
      </div>
      <div class="video-player-subtitle-status">
        当前字幕：{{ currentSubtitleLabel }}
      </div>
    </div>
  </div>
</template>

<script>

import 'video.js/dist/video-js.css'

import 'vue-video-player/src/custom-theme.css'

import { videoPlayer } from 'vue-video-player'

import 'videojs-contrib-hls'

import {

  SUBTITLE_OFF,

  buildSubtitleOptions,

  buildSubtitleOptionsFromTextTracks,

  buildVideoSubtitleTracks

} from '@/utils/previewSubtitle'



export default {

  name: 'VideoPlayer',

  components: {

    videoPlayer

  },

  props: {

    url: {

      type: String,

      required: true

    },

    posterUrl: {

      type: String,

      default: ''

    },

    autoplay: {

      type: Boolean,

      default: false

    },

    muted: {

      type: Boolean,

      default: false

    },

    /** 在线预览：展示字幕 Tab，并由 Tab 控制轨（不自动打开第一条字幕） */

    previewSubtitleTabs: {

      type: Boolean,

      default: false

    },

    /** 外挂字幕列表，项含 subtitleUrl / language / languageName */

    subtitles: {

      type: Array,

      default: () => []

    },

    /** 当前选中的字幕 language；关闭为 off（v-model） */

    value: {

      type: String,

      default: SUBTITLE_OFF

    },

    /** 无接口 subtitles 时，用 m3u8 内嵌 HLS 字幕轨生成 Tab（短剧管理预览） */

    useHlsEmbeddedSubtitles: {

      type: Boolean,

      default: false

    }

  },

  data() {

    return {

      player: null,

      subtitleTrackList: null,

      subtitleTrackTimer: null,

      _previewTracksHooked: false,

      hlsSubtitleTabOptions: []

    }

  },

  computed: {

    videoType() {

      const url = (this.url || '').toLowerCase()

      if (url.includes('.m3u8')) return 'application/x-mpegURL'

      if (url.includes('.mp4')) return 'video/mp4'

      if (url.includes('.webm')) return 'video/webm'

      if (url.includes('.ogg') || url.includes('.ogv')) return 'video/ogg'

      if (url.includes('.avi')) return 'video/x-msvideo'

      if (url.includes('.mov')) return 'video/quicktime'

      return 'video/mp4'

    },

    playerOptions() {

      const options = {

        autoplay: this.autoplay,

        muted: this.muted,

        loop: false,

        preload: 'auto',

        language: 'zh-CN',

        crossorigin: 'anonymous',

        html5: {

          hls: {

            withCredentials: false,

            overrideNative: true

          },

          nativeAudioTracks: false,

          nativeVideoTracks: false,

          vhs: { withCredentials: false }

        },

        sources: [

          {

            type: this.videoType,

            src: this.url

          }

        ],

        notSupportedMessage: '此视频暂无法播放，请稍后再试',

        controlBar: {

          remainingTimeDisplay: {

            displayNegative: false

          }

        }

      }

      if (this.previewSubtitleTabs) {

        options.fluid = false

        options.fill = true

      } else {

        options.aspectRatio = '16:9'

        options.fluid = true

      }

      if (this.posterUrl) {

        options.poster = this.posterUrl

      }

      return options

    },

    usesApiSubtitleTracks() {

      return (this.videoSubtitleTracks || []).length > 0

    },

    subtitleTabOptions() {

      if (!this.previewSubtitleTabs) return []

      const apiOptions = buildSubtitleOptions(this.subtitles)

      if (apiOptions.length > 1) return apiOptions

      if (this.useHlsEmbeddedSubtitles && this.hlsSubtitleTabOptions.length) {

        return [

          { value: SUBTITLE_OFF, label: '关闭' },

          ...this.hlsSubtitleTabOptions

        ]

      }

      return apiOptions

    },

    videoSubtitleTracks() {

      return buildVideoSubtitleTracks(this.subtitles)

    },

    subtitleActive: {

      get() {

        return this.value || SUBTITLE_OFF

      },

      set(next) {

        this.$emit('input', next)

      }

    },

    currentSubtitleLabel() {

      const hit = this.subtitleTabOptions.find(

        (item) => item.value === this.subtitleActive

      )

      return (hit && hit.label) || '关闭'

    }

  },

  watch: {

    url() {

      this._previewTracksHooked = false

      this.hlsSubtitleTabOptions = []

    },

    subtitles: {

      handler() {

        this.ensureSubtitleValueValid()

        if (this.previewSubtitleTabs && this.player) {

          this.applyPreviewRemoteSubtitleTracks()

        }

      },

      deep: true

    },

    subtitleActive() {

      this.syncPreviewSubtitleTracks()

    }

  },

  methods: {

    onPlayerReady(player) {

      if (!player) return



      this.clearSubtitleListeners()

      this.player = player

      this.subtitleTrackList = player.textTracks()

      player.on('dispose', this.onPlayerDispose)



      if (this.previewSubtitleTabs) {

        if (!this._previewTracksHooked) {

          player.off('loadedmetadata', this.onPreviewLoadedMetadata)

          player.on('loadedmetadata', this.onPreviewLoadedMetadata)

          this._previewTracksHooked = true

        }

        if (this.usesApiSubtitleTracks) {

          this.applyPreviewRemoteSubtitleTracks()

        } else if (this.useHlsEmbeddedSubtitles) {

          this.subtitleTrackList.addEventListener(

            'addtrack',

            this.onHlsSubtitleTrackAdd

          )

          this.refreshHlsSubtitleTabs()

        }

      } else {

        player.on('loadedmetadata', this.scheduleSubtitleTrackEnable)

        this.subtitleTrackList.addEventListener(

          'addtrack',

          this.scheduleSubtitleTrackEnable

        )

        this.scheduleSubtitleTrackEnable()

      }

    },

    onPlayerDispose() {

      this.clearSubtitleListeners()

      this.player = null

    },

    clearSubtitleListeners() {

      if (this.subtitleTrackTimer !== null) {

        clearTimeout(this.subtitleTrackTimer)

        this.subtitleTrackTimer = null

      }

      if (this.player) {

        this.player.off('loadedmetadata', this.scheduleSubtitleTrackEnable)

        this.player.off('loadedmetadata', this.onPreviewLoadedMetadata)

        this.player.off('dispose', this.onPlayerDispose)

      }

      if (this.subtitleTrackList) {

        this.subtitleTrackList.removeEventListener(

          'addtrack',

          this.scheduleSubtitleTrackEnable

        )

        this.subtitleTrackList.removeEventListener(

          'addtrack',

          this.onHlsSubtitleTrackAdd

        )

        this.subtitleTrackList = null

      }

      this._previewTracksHooked = false

      this.hlsSubtitleTabOptions = []

    },

    ensureSubtitleValueValid() {

      if (!this.previewSubtitleTabs) return

      if (

        this.subtitleActive !== SUBTITLE_OFF &&

        !this.subtitleTabOptions.some(

          (item) => item.value === this.subtitleActive

        )

      ) {

        this.subtitleActive = SUBTITLE_OFF

      }

    },

    handleSubtitleTabClick(next) {

      if (next === this.subtitleActive) return

      this.subtitleActive = next

    },

    onPreviewLoadedMetadata() {

      if (this.usesApiSubtitleTracks) {

        this.applyPreviewRemoteSubtitleTracks()

      } else if (this.useHlsEmbeddedSubtitles) {

        this.refreshHlsSubtitleTabs()

      }

    },

    onHlsSubtitleTrackAdd() {

      if (!this.useHlsEmbeddedSubtitles || this.usesApiSubtitleTracks) return

      this.refreshHlsSubtitleTabs()

    },

    refreshHlsSubtitleTabs() {

      if (!this.player || !this.useHlsEmbeddedSubtitles) return

      if (this.usesApiSubtitleTracks) return

      this.hlsSubtitleTabOptions = buildSubtitleOptionsFromTextTracks(this.player)

      this.ensureSubtitleValueValid()

      this.syncPreviewSubtitleTracks()

      setTimeout(() => this.syncPreviewSubtitleTracks(), 120)

    },

    clearPreviewRemoteSubtitleTracks() {

      if (!this.player) return

      const remote = this.player.remoteTextTracks()

      for (let i = remote.length - 1; i >= 0; i -= 1) {

        this.player.removeRemoteTextTrack(remote[i])

      }

    },

    applyPreviewRemoteSubtitleTracks() {

      if (!this.previewSubtitleTabs || !this.player) return

      if (!this.usesApiSubtitleTracks) return

      this.clearPreviewRemoteSubtitleTracks()

      this.videoSubtitleTracks.forEach((item) => {

        if (!item || !item.src) return

        this.player.addRemoteTextTrack(

          {

            kind: 'subtitles',

            src: item.src,

            srclang: item.language,

            label: item.label

          },

          false

        )

      })

      this.syncPreviewSubtitleTracks()

      setTimeout(() => this.syncPreviewSubtitleTracks(), 80)

    },

    syncPreviewSubtitleTracks() {

      if (!this.previewSubtitleTabs || !this.player) return

      const tracks = this.player.textTracks()

      for (let i = 0; i < tracks.length; i += 1) {

        const track = tracks[i]

        if (track.kind !== 'subtitles' && track.kind !== 'captions') continue

        if (this.subtitleActive === SUBTITLE_OFF) {

          track.mode = 'hidden'

        } else {

          track.mode =

            track.language === this.subtitleActive ? 'showing' : 'hidden'

        }

      }

    },

    scheduleSubtitleTrackEnable() {

      if (this.previewSubtitleTabs) return

      if (this.subtitleTrackTimer !== null) return



      this.subtitleTrackTimer = setTimeout(() => {

        this.subtitleTrackTimer = null

        this.enableSubtitleTrack()

      }, 0)

    },

    enableSubtitleTrack() {

      if (!this.player || this.previewSubtitleTabs) return



      const tracks = this.player.textTracks()

      let firstCaptionTrack = null

      for (let index = 0; index < tracks.length; index++) {

        const track = tracks[index]

        if (track.kind === 'subtitles') {

          if (track.mode === 'showing') return

          track.mode = 'showing'

          return

        }

        if (!firstCaptionTrack && track.kind === 'captions') {

          if (track.mode === 'showing') return

          firstCaptionTrack = track

        }

      }



      if (firstCaptionTrack) firstCaptionTrack.mode = 'showing'

    },

    onError(e) {

      console.log(e)

    },

    onPlayerPlay(player) {

      this.$emit('play', player)

    },

    onPlayerPause(player) {

      this.$emit('pause', player)

    },

    onPlayerEnded(player) {

      this.$emit('ended', player)

    },

    play() {

      if (this.player && typeof this.player.play === 'function') {

        try {

          this.player.play()

        } catch (e) {}

      }

    },

    pause() {

      if (this.player && typeof this.player.pause === 'function') {

        try {

          this.player.pause()

        } catch (e) {}

      }

    },

    dispose() {

      if (this.player) {

        try {

          if (typeof this.player.pause === 'function') this.player.pause()

        } catch (e) {}

        try {

          if (typeof this.player.dispose === 'function') this.player.dispose()

        } catch (e) {}

        this.player = null

      }

    }

  },

  beforeDestroy() {

    this.onPlayerDispose()

  }

}

</script>



<style scoped lang="less">

.video-player-root {

  width: 100%;

  height: 100%;

}



.video-player-root--preview-tabs {

  height: auto;

}



.video-player-root--preview-tabs .video-player-wrapper {

  height: 700px;

  background: #000;

}



.video-player-root--preview-tabs /deep/ .video-js {

  width: 100% !important;

  height: 100% !important;

  padding-top: 0 !important;

  background: #000;

}



.video-player-root--preview-tabs /deep/ .video-js.vjs-fluid {

  padding-top: 0 !important;

}



.video-player-root--preview-tabs /deep/ .vjs-tech {

  object-fit: contain;

  width: 100%;

  height: 100%;

}



.video-player-wrapper {

  width: 100%;

  margin: 0 auto;

  height: 100%;

}



.video-player {

  width: 100%;

  height: 100%;

}



/deep/.video-js {

  height: 100%;

}



/deep/.vjs-volume-panel {

  display: none;

}



/deep/.video-js.vjs-layout-tiny:not(.vjs-fullscreen) .vjs-subs-caps-button:not(.vjs-hidden),

/deep/.video-js.vjs-layout-x-small:not(.vjs-fullscreen) .vjs-subs-caps-button:not(.vjs-hidden) {

  display: block;

}



/deep/.vjs-text-track-cue {

  white-space: pre-wrap !important;

  word-break: break-word;

  max-width: 92% !important;

  width: auto !important;

  box-sizing: border-box;

}



.video-player-subtitle-block {

  width: 393.75px;

  max-width: 100%;

  margin-top: 16px;

}



.video-player-subtitle-row {

  display: flex;

  align-items: center;

  flex-wrap: wrap;

  gap: 12px;

}



.video-player-subtitle-label {

  flex-shrink: 0;

  font-size: 14px;

  line-height: 22px;

  color: #303133;

}



.video-player-subtitle-tabs {

  display: flex;

  flex-wrap: wrap;

  gap: 8px;

}



.video-player-subtitle-tab {

  min-width: 72px;

  height: 32px;

  padding: 0 14px;

  font-size: 13px;

  line-height: 30px;

  color: #606266;

  background: #fff;

  border: 1px solid #dcdfe6;

  border-radius: 4px;

  cursor: pointer;

  outline: none;

  transition: color 0.15s, border-color 0.15s, background 0.15s;

}



.video-player-subtitle-tab:hover {

  color: #409eff;

  border-color: #c6e2ff;

}



.video-player-subtitle-tab--active {

  color: #409eff;

  border-color: #409eff;

  background: #ecf5ff;

}



.video-player-subtitle-status {

  margin-top: 10px;

  font-size: 13px;

  line-height: 20px;

  color: #909399;

}

</style>


